<script setup>
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useToggle } from '@vueuse/core';
import { useAlert } from 'dashboard/composables';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ContactListCard from './ContactListCard.vue';
import ContactListDialog from './ContactListDialog.vue';
import ContactListEmptyState from './ContactListEmptyState.vue';
import ConfirmDeleteDialog from './ConfirmDeleteDialog.vue';
import ContactListContactsDialog from './ContactListContactsDialog.vue';

const { t } = useI18n();
const store = useStore();

const selectedList = ref(null);
const [showDialog, toggleDialog] = useToggle();
const [isEditing, setIsEditing] = useToggle();
const [showContactsDialog, toggleContactsDialog] = useToggle();
const confirmDeleteRef = ref(null);

const uiFlags = computed(() => store.getters['contactLists/getUIFlags']);
const isFetching = computed(() => uiFlags.value.isFetching);
const contactLists = computed(() => store.getters['contactLists/getContactLists']);
const hasNoLists = computed(() => contactLists.value?.length === 0 && !isFetching.value);

onMounted(() => {
  store.dispatch('contactLists/get');
});

const handleCreate = () => {
  selectedList.value = null;
  setIsEditing(false);
  toggleDialog(true);
};

const handleEdit = list => {
  selectedList.value = list;
  setIsEditing(true);
  toggleDialog(true);
};

const handleDelete = list => {
  selectedList.value = list;
  confirmDeleteRef.value?.open();
};

const handleManageContacts = list => {
  selectedList.value = list;
  toggleContactsDialog(true);
};

const handleCloseDialog = () => {
  toggleDialog(false);
  selectedList.value = null;
  setIsEditing(false);
};

const handleCloseContactsDialog = () => {
  toggleContactsDialog(false);
  selectedList.value = null;
};

const confirmDelete = async () => {
  if (!selectedList.value) return;

  try {
    await store.dispatch('contactLists/delete', selectedList.value.id);
    useAlert(t('CONTACT_LISTS.API.DELETE_SUCCESS'));
    confirmDeleteRef.value?.close();
    selectedList.value = null;
  } catch (error) {
    useAlert(t('CONTACT_LISTS.API.ERROR'));
  }
};
</script>

<template>
  <div class="flex flex-col h-full bg-n-background">
    <header class="flex items-center justify-between px-6 py-4 border-b border-n-weak">
      <h1 class="text-xl font-semibold text-n-slate-12">
        {{ t('CONTACT_LISTS.HEADER_TITLE') }}
      </h1>
      <Button
        icon="i-lucide-plus"
        color-scheme="primary"
        @click="handleCreate"
      >
        {{ t('CONTACT_LISTS.NEW_LIST') }}
      </Button>
    </header>

    <div class="flex-1 overflow-auto p-6">
      <div
        v-if="isFetching"
        class="flex items-center justify-center py-10 text-n-slate-11"
      >
        <Spinner />
      </div>

      <div
        v-else-if="!hasNoLists"
        class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
      >
        <ContactListCard
          v-for="list in contactLists"
          :key="list.id"
          :list="list"
          @edit="handleEdit"
          @delete="handleDelete"
          @manage-contacts="handleManageContacts"
        />
      </div>

      <ContactListEmptyState
        v-else
        @create="handleCreate"
      />
    </div>

    <ContactListDialog
      v-if="showDialog"
      :list="selectedList"
      :is-editing="isEditing"
      @close="handleCloseDialog"
    />

    <ContactListContactsDialog
      v-if="showContactsDialog"
      :list="selectedList"
      @close="handleCloseContactsDialog"
    />

    <ConfirmDeleteDialog
      ref="confirmDeleteRef"
      :list="selectedList"
      @confirm="confirmDelete"
    />
  </div>
</template>
